import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Intent } from '@blueprintjs/core';
import { LinkButton } from 'components/buttons';
import { DateToLocal } from 'components/dates';
import { ShowHide } from 'components/layout';
import { Breadcrumbs, VerticalLinkGroup } from 'components/navigation';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { InstantReportButton } from 'components/reporting';
import { Tooltip } from 'components/tooltip';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { getEditBlockedMessage } from 'pages/employee-reviews/shared';
import { PageRestricted } from 'pages/errors/page-restricted';
import { EmployeeReviewsService } from 'services';
import { CompletedReviewDetail } from './CompletedReviewDetail';
import { SignOffResend } from './SignOffResend';
import { ReviewHistory } from './review-history';

const requiredAction = "EmployeeReview:View";
const editAction = "EmployeeReview:Perform";
const listingRoute = "/employee-reviews/completed-reviews";
const reReadFailureMessage = "Failed to refresh this review. The details shown may be out of date.";
const reportGenerationFailureMessage = "Failed to generate report. Please try again.";
const dateFormat = "DD MMM YYYY";
const pdfUnavailableMessage = "This review cannot be downloaded as a PDF because it includes topics you do not have permission to view.";

export function EmployeeReviewView(props) {

    const { reviewId } = useParams();
    const canViewReview = UserHasAccess(requiredAction);
    const canEditReview = UserHasAccess(editAction);

    const [loading, setLoading] = useState(true);
    const [invalidReview, setInvalidReview] = useState(false);
    const [review, setReview] = useState(null);
    const [requestingReport, setRequestingReport] = useState(false);
    const [reportGenerationId, setReportGenerationId] = useState(null);

    const baseRoute = `${listingRoute}/${reviewId}`;
    const reviewRoute = `${baseRoute}/review`;
    const tabRoutes = [
        { location: reviewRoute, title: "Review" },
        { location: `${baseRoute}/history`, title: "History" }
    ];

    // The completed review is an auditable point-in-time record: everything on the page renders
    // from the returned snapshot, with no live employee call. It is re-read after a sign-off
    // email is sent, because the signature URLs and the resend eligibility are both derived by
    // the API rather than held on the page.
    //
    // The read is returned so a caller that triggered it can wait for the record to land before
    // it re-enables anything acting on the old one. Rejection is handled here, so what the
    // caller waits on always settles.
    //
    // Only the first read decides whether the review can be shown at all. A re-read that fails
    // leaves the record the page already holds on screen and says it could not be refreshed,
    // because there is no longer a reason to believe the review is unreachable.
    const loadReview = useCallback((isInitialLoad = false) => {
        if (!canViewReview) {
            return Promise.resolve();
        }

        return EmployeeReviewsService.getCompletedReview(reviewId).then((reviewResponse) => {
            setReview(reviewResponse);
        }, () => {
            if (isInitialLoad) {
                setInvalidReview(true);
                return;
            }

            NotificationToaster.show(Intent.DANGER, reReadFailureMessage);
        }).finally(() => {
            setLoading(false);
        });
    }, [reviewId, canViewReview]);

    useEffect(() => {
        loadReview(true);
    }, [loadReview]);

    function generateReport() {
        setRequestingReport(true);

        EmployeeReviewsService.generateCompletedReviewReport(Number(reviewId)).then((generationId) => {
            setReportGenerationId(generationId);
        }, () => {
            setReportGenerationId(null);
            NotificationToaster.show(Intent.DANGER, reportGenerationFailureMessage);
        }).finally(() => {
            setRequestingReport(false);
        });
    }

    // A loaded review that cannot be edited says why; nothing is said while it loads.
    let editBlockedMessage = null;

    if (review != null && review.canEdit !== true) {
        editBlockedMessage = getEditBlockedMessage(review.editBlockedReason);
    }

    // Held disabled from the queue request until the file has downloaded or failed, so a second
    // PDF cannot be requested while one is being produced.
    const reportButtonDisabled = loading
        || review?.canDownloadPdf !== true
        || requestingReport
        || reportGenerationId != null;

    // The title and the actions on the review as a whole sit above the tabs, so both tabs share them.
    function renderHeaderActions() {
        return (
            <Fragment>
                <Tooltip
                    content={pdfUnavailableMessage}
                    position="top"
                    disabled={review?.canDownloadPdf !== false}
                >
                    <span>
                        <InstantReportButton
                            id="download-review-pdf"
                            text="Report"
                            icon={<i className="fa fa-file-pdf-o"></i>}
                            onReportGenerate={generateReport}
                            onReportDownloadComplete={() => setReportGenerationId(null)}
                            buttonDisabled={reportButtonDisabled}
                            reportId={reportGenerationId}
                        />
                    </span>
                </Tooltip>
                {/* canEdit is advisory: the edit page re-checks it on load and the API refuses an edit it does not allow.
                    A disabled button fires no hover events, so the span gives the reason's tooltip its target. */}
                <ShowHide
                    evaluator={canEditReview}
                    show={(
                        <Tooltip
                            content={editBlockedMessage ?? ""}
                            position="top"
                            disabled={editBlockedMessage == null}
                        >
                            <span>
                                <LinkButton
                                    id="edit-review"
                                    icon="edit"
                                    text="Edit"
                                    intent="secondary"
                                    disabled={review?.canEdit !== true}
                                    href={`${baseRoute}/edit`}
                                />
                            </span>
                        </Tooltip>
                    )}
                />
            </Fragment>
        );
    }

    function renderReview() {
        return (
            <CompletedReviewDetail
                review={review}
                loading={loading}
                showHeader={false}
                signOffActions={(
                    <SignOffResend
                        reviewId={reviewId}
                        signOff={review?.signOff}
                        onSent={loadReview}
                    />
                )}
            />
        );
    }

    // The change log is read from the review's completion date, so it waits for the review to land.
    function renderHistory() {
        return (
            <ShowHide
                evaluator={review != null}
                show={(
                    <ReviewHistory reviewId={reviewId} completedDate={review?.completedDate} />
                )}
            />
        );
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <ShowHide
                    evaluator={invalidReview}
                    show={(
                        <div className="row">
                            <Breadcrumbs items={props.breadcrumbs} />
                            <NotificationInline
                                allowClose={false}
                                show
                                text="This review does not exist, or you do not have permission to view it"
                                intent="danger"
                            />
                            <div>
                                <LinkButton intent="primary" text="Back to Completed Reviews" id="listing-completed-reviews" href={listingRoute} />
                            </div>
                        </div>
                    )}
                    hide={(
                        <Fragment>
                            <div className="row">
                                <Breadcrumbs items={props.breadcrumbs} />
                                <div className={classNames("inline-items", "spacer-bottom", { "bp6-skeleton": loading })}>
                                    <h1 className="no-margin">{review?.reviewType?.name} - <DateToLocal format={dateFormat}>{review?.completedDate}</DateToLocal></h1>
                                </div>
                                <div className="button-row spacer-bottom">
                                    {renderHeaderActions()}
                                </div>
                            </div>
                            <div className="layout-cols">
                                <div className="col side-col">
                                    <div className="row">
                                        <VerticalLinkGroup links={tabRoutes} />
                                    </div>
                                </div>
                                <div className="col content-col">
                                    <Routes>
                                        <Route path="review" element={renderReview()} />
                                        <Route path="history" element={renderHistory()} />
                                        <Route path="" element={<Navigate to={reviewRoute} replace />} />
                                    </Routes>
                                </div>
                            </div>
                        </Fragment>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EmployeeReviewView.propTypes = {
    breadcrumbs: PropTypes.array
};
